import { Loader, Menu, Tabs } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDots, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { FirewallSecurityGroupResponse } from '../../../../../../@types/firewall-security-group';
import { Button, ConfirmActionModal } from '../../../../../../components/Atoms';
import DialogBox from '../../../../../../components/Atoms/DialogBox/DialogBox';
import { useDeleteSecurityGroupMutate } from '../../../../../../hooks/CloudComputeHooks/useDeleteSecurityGroupMutate';
import { useSecurityGroupsInfiniteQuery } from '../../../../../../hooks/CloudComputeHooks/useSecurityGroupsInfiniteQuery';
import SecurityGroupModal from './SecurityGroupModal';

export default function SecurityGroupListTab() {
    const { t } = useTranslation('cloudCompute');
    const navigate = useNavigate();
    const { data, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
        useSecurityGroupsInfiniteQuery(20);
    const deleteMutation = useDeleteSecurityGroupMutate();

    const [groupModalOpened, { open: openGroupModal, close: closeGroupModal }] = useDisclosure();
    const [editingGroup, setEditingGroup] = useState<FirewallSecurityGroupResponse | null>(null);

    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure();
    const [deleteTarget, setDeleteTarget] = useState<FirewallSecurityGroupResponse | null>(null);

    const items = useMemo(() => data?.pages.flatMap((p) => p.rows) ?? [], [data]);
    const total = data?.pages[0]?.total_rows ?? items.length;

    const openCreate = () => {
        setEditingGroup(null);
        openGroupModal();
    };

    const openEdit = (group: FirewallSecurityGroupResponse) => {
        setEditingGroup(group);
        openGroupModal();
    };

    const askDelete = (group: FirewallSecurityGroupResponse) => {
        setDeleteTarget(group);
        openDelete();
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        deleteMutation.mutate(deleteTarget.id, {
            onSuccess: () => {
                closeDelete();
                setDeleteTarget(null);
            },
        });
    };

    const createButton = (
        <Button size="sm" variant="primary" leftIcon={<IconPlus size={16} />} onClick={openCreate}>
            {t('securityGroups.createGroup')}
        </Button>
    );

    return (
        <Tabs.Panel value={'security-group'} className="w-full">
            <DialogBox
                label={t('securityGroups.title')}
                description={t('securityGroups.totalSuffix', { count: total })}
                rightDescription={createButton}
                classNames=""
            >
                {isPending && !data ? (
                    <div className="w-full p-8 flex justify-center">
                        <Loader size={20} color="var(--fg)" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="w-full p-8 text-sm text-fg/68 text-center">
                        {t('securityGroups.empty', {
                            button: t('securityGroups.emptyButton'),
                        })}
                    </div>
                ) : (
                    <>
                        {items.map((sg) => (
                            <div
                                key={sg.id}
                                className="w-full border-t-[0.5px] border-line first:border-t-0 flex flex-row items-center hover:bg-surface-3 transition-colors cursor-pointer"
                            >
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(`/cloud-compute/security-groups/${sg.id}`)
                                    }
                                    className="flex-1 text-left px-4 py-3 flex flex-row items-start justify-between gap-3"
                                >
                                    <div className="flex flex-col min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-base text-fg font-medium truncate">
                                                {sg.name}
                                            </span>
                                            <span className="text-xs text-fg/68 font-mono shrink-0">
                                                {t('securityGroups.inboundOutboundSummary', {
                                                    inbound: sg.in_rule_count,
                                                    outbound: sg.out_rule_count,
                                                })}
                                            </span>
                                        </div>
                                        {sg.description && (
                                            <div className="text-sm text-fg/68 mt-1.5 truncate">
                                                {sg.description}
                                            </div>
                                        )}
                                    </div>
                                </button>
                                <div className="pr-3">
                                    <Menu position="bottom-end" withinPortal>
                                        <Menu.Target>
                                            <button
                                                type="button"
                                                aria-label={t('securityGroups.groupActions')}
                                                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-fg/68 hover:bg-surface-3 hover:text-fg transition-colors"
                                            >
                                                <IconDots size={16} />
                                            </button>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Item
                                                leftSection={<IconPencil size={14} />}
                                                onClick={() => openEdit(sg)}
                                            >
                                                {t('securityGroups.rename')}
                                            </Menu.Item>
                                            <Menu.Item
                                                color="var(--color-status-danger)"
                                                leftSection={<IconTrash size={14} />}
                                                onClick={() => askDelete(sg)}
                                            >
                                                {t('securityGroups.delete')}
                                            </Menu.Item>
                                        </Menu.Dropdown>
                                    </Menu>
                                </div>
                            </div>
                        ))}
                        {hasNextPage && (
                            <div className="w-full flex justify-center px-4 py-3 border-t-[0.5px] border-line">
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => fetchNextPage()}
                                    disabled={isFetchingNextPage}
                                >
                                    {isFetchingNextPage
                                        ? t('securityGroups.loading')
                                        : t('securityGroups.loadMore')}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </DialogBox>

            <SecurityGroupModal
                opened={groupModalOpened}
                onClose={() => {
                    closeGroupModal();
                    setEditingGroup(null);
                }}
                group={editingGroup ?? undefined}
            />
            <ConfirmActionModal
                opened={deleteOpened}
                onClose={closeDelete}
                title={t('securityGroups.deleteTitle')}
                message={
                    deleteTarget
                        ? t('securityGroups.deleteMessage', { name: deleteTarget.name })
                        : ''
                }
                confirmLabel={t('securityGroups.deleteConfirm')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </Tabs.Panel>
    );
}
